import { Button, InputBox } from '@imprivata-cloud/components';
import { Form } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContinueButton from '../../../components/continueButton/ContinueButton';
import { STEPS } from '../../constants';
import { useNavigateToNextStep } from '../../hooks/useNavigateToNextStep';
import './ImprivataIdp.less';
import { CodingContext, getRSAPublicKey } from '@imprivata-cloud/dataprivacy';
import { findImprIdpDirectories } from '../../../api/setupServices';
import type { ImprDir } from '../../../api/types';
import { _getUrl } from '../../../api/utils';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import type { AppError } from '../../../errorHandler/errors';
import { useAddImprIdpDirectory } from '../../hooks/useImprivataIdp';

const TRANSLATION_ROOT = `setup.${STEPS.IMPRIVATA_IDP}.content`;
const SUBTITLE_CLASSNAME = 'impr-idp-subtitle';

export interface ImprIdpFormValues {
  domainName: string;
  username: string;
  password: string;
}

const ImprivataIdp = () => {
  const navigateToNextStep = useNavigateToNextStep();
  const { mutate: addIdpDirectory } = useAddImprIdpDirectory();
  const [form] = Form.useForm<ImprIdpFormValues>();
  const { t } = useTranslation();
  const [domainName, setDomainName] = useState<string>();
  const [directory, setDirectory] = useState<ImprDir>();
  const { emitError } = useErrorContext();

  const fetchData = useCallback(async () => {
    try {
      const response = await findImprIdpDirectories();
      if (response?.directories && response.directories.length > 0) {
        const directories = response.directories;
        if (directories[0].domains?.length > 0) {
          form.setFieldValue('domainName', directories[0].domains[0].domainName);
          setDomainName(directories[0].domains[0].domainName);
        }
        setDirectory(directories[0]);
      }
    } catch (error) {
      emitError(error as AppError);
    }
  }, [form, emitError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const validateForm = () => {
    const values = form.getFieldsValue();
    if (!values) {
      return false;
    }
    return !(values.domainName?.length >= 3 && values.username?.length >= 6 && values.password?.length >= 16);
  };

  const onSubmit = async (values: ImprIdpFormValues) => {
    const { metadataUrl } = _getUrl({ metadataUrl: { sub: 'metadata.app' } });
    const publicDev = await getRSAPublicKey(`${metadataUrl}/data-privacy/public.json`);
    const codingCtx = await CodingContext.createContextV2(publicDev.key, publicDev.version);
    const domainName = `${values?.domainName?.trim()}.imprivata.id`;
    const username = `${values?.username?.trim()}@${domainName}`;
    const password = codingCtx.encryptString(values?.password);

    addIdpDirectory({
      addDirectoryRequest: {
        directory: {
          domains: [{ domainName: domainName, isPrimary: true }],
          directoryId: directory?.directoryId,
          name: domainName,
        },
        initialUser: {
          username: username,
          password: password,
        },
      },
      codingContext: codingCtx,
    });
  };

  return (
    <div>
      <div className={SUBTITLE_CLASSNAME}>
        {t(`${TRANSLATION_ROOT}.subtitle`)}{' '}
        <a href="https://docs.imprivata.com/" target="_blank" rel="noopener noreferrer">
          {t('common.see-instructions')}
        </a>
      </div>
      <Form form={form} layout="vertical" requiredMark={false} aria-label={'impr-idp-form'} onFinish={onSubmit}>
        <div className={'impr-idp-input'}>
          <Form.Item name={'domainName'} rules={[{ required: true }]} className={'impr-idp-form-item'}>
            <InputBox
              autoFocus
              size="large"
              label={'Domain'}
              className={'input'}
              minLength={3}
              maxLength={63}
              onChange={(e) => setDomainName(e.target.value)}
            />
          </Form.Item>
          <span>.imprivata.id</span>
        </div>
        <div className={SUBTITLE_CLASSNAME}>{t(`${TRANSLATION_ROOT}.acct-label`)} </div>
        <div className={'impr-idp-input'}>
          <Form.Item label={'Username'} name={'username'} rules={[{ required: true }]} className={'impr-idp-form-item'}>
            <InputBox size="large" aria-label={'username'} className={'input'} minLength={6} maxLength={30} />
          </Form.Item>
          <span>{domainName ? `@${domainName}.imprivata.id` : ''}</span>
        </div>
        <Form.Item label={'Password'} name={'password'} rules={[{ required: true }]}>
          <div className={'impr-idp-password'}>
            <InputBox
              size="large"
              aria-label={'password'}
              className={'input'}
              minLength={16}
              maxLength={30}
              type="password"
            />
            <p>{t(`${TRANSLATION_ROOT}.password-hint`)}</p>
          </div>
        </Form.Item>
        <div className={SUBTITLE_CLASSNAME}>{t(`${TRANSLATION_ROOT}.info-text`)} </div>
        <Form.Item className={'impr-idp-continue-button'}>
          {/* Continue */}
          <ContinueButton
            label={'Continue'}
            htmlType="submit"
            overrideErrorCheck={() => validateForm()}
            validateDirty={['username', 'password']}
          />
        </Form.Item>
        <Button
          label={t('common.skip')}
          onClick={() => navigateToNextStep(STEPS.EXTERNAL_IDP_INFO)}
          size="large"
          className={'impr-idp-skip-button'}
        />
      </Form>
    </div>
  );
};

export default ImprivataIdp;
